import type { IconType } from 'react-icons';
import {
  FaEnvelope,
  FaFacebookMessenger,
  FaLine,
  FaShopify,
  FaSlack,
  FaSquarespace,
  FaTelegram,
  FaWebflow,
  FaWhatsapp,
  FaWix,
  FaWordpress,
} from 'react-icons/fa6';

import clsxm from '@/lib/clsxm';

import type { ChannelKey } from './types';
import { useChannelsT } from './useChannelsT';

/**
 * Channels whose page is live. A channel joins the cross-links, the sitemap
 * and the i18n.json route only once its page exists.
 */
export const LIVE_CHANNELS: ChannelKey[] = [
  'whatsapp',
  'telegram',
  'messenger',
  'slack',
  'line',
  'email',
  'wordpress',
  'shopify',
  'wix',
  'squarespace',
  'webflow',
];

const LOGOS: Record<ChannelKey, { Icon: IconType; color: string }> = {
  whatsapp: { Icon: FaWhatsapp, color: 'text-[#25D366]' },
  telegram: { Icon: FaTelegram, color: 'text-[#229ED9]' },
  messenger: { Icon: FaFacebookMessenger, color: 'text-[#0084FF]' },
  slack: { Icon: FaSlack, color: 'text-[#4A154B]' },
  line: { Icon: FaLine, color: 'text-[#06C755]' },
  email: { Icon: FaEnvelope, color: 'text-primary-600' },
  wordpress: { Icon: FaWordpress, color: 'text-[#21759B]' },
  shopify: { Icon: FaShopify, color: 'text-[#95BF47]' },
  wix: { Icon: FaWix, color: 'text-[#0f0f0f]' },
  squarespace: { Icon: FaSquarespace, color: 'text-[#0f0f0f]' },
  webflow: { Icon: FaWebflow, color: 'text-[#146EF5]' },
};

/** A channel's logo in its brand colour. Sits on a white tile in both themes. */
export function ChannelLogo({
  channel,
  className,
}: {
  channel: ChannelKey;
  className?: string;
}) {
  const { Icon, color } = LOGOS[channel];
  return (
    <Icon aria-hidden='true' className={clsxm('h-5 w-5', color, className)} />
  );
}

export type ChannelEntry = {
  key: ChannelKey;
  /** Brand names stay as they are in every language. */
  name: string;
  summary: string;
  href: string;
};

/** The channel pages, as their cross-links describe them. */
export function useChannelCatalog(): ChannelEntry[] {
  const { t } = useChannelsT();

  const all: ChannelEntry[] = [
    {
      key: 'whatsapp',
      name: 'WhatsApp',
      summary: t('Answers the messages your WhatsApp Business number gets.'),
      href: '/chatbots/whatsapp',
    },
    {
      key: 'telegram',
      name: 'Telegram',
      summary: t('A Telegram bot that answers from your own knowledge.'),
      href: '/chatbots/telegram',
    },
    {
      key: 'messenger',
      name: 'Messenger',
      summary: t('Replies to the messages your Facebook Page receives.'),
      href: '/chatbots/messenger',
    },
    {
      key: 'slack',
      name: 'Slack',
      summary: t('Answers your team’s questions in Slack.'),
      href: '/chatbots/slack',
    },
    {
      key: 'line',
      name: 'LINE',
      summary: t('Answers your LINE Official Account’s chats.'),
      href: '/chatbots/line',
    },
    {
      key: 'email',
      name: t('Email'),
      summary: t('Replies to customer emails in the same thread.'),
      href: '/chatbots/email',
    },
    {
      key: 'wordpress',
      name: 'WordPress',
      summary: t('A chat bubble on your WordPress site, with a plugin.'),
      href: '/chatbots/wordpress',
    },
    {
      key: 'shopify',
      name: 'Shopify',
      summary: t('Finds products and shares order status in your store.'),
      href: '/chatbots/shopify',
    },
    {
      key: 'wix',
      name: 'Wix',
      summary: t('Adds the chat to every page of your Wix site.'),
      href: '/chatbots/wix',
    },
    {
      key: 'squarespace',
      name: 'Squarespace',
      summary: t('Adds the chat to your Squarespace site.'),
      href: '/chatbots/squarespace',
    },
    {
      key: 'webflow',
      name: 'Webflow',
      summary: t('Adds the chat to your Webflow site.'),
      href: '/chatbots/webflow',
    },
  ];

  return all.filter(({ key }) => LIVE_CHANNELS.includes(key));
}
